/**
 * NMS Product Description Modal Component
 * Handles modal functionality for additional product information
 * @extends NMSComponent
 */

class NMSProductDescriptionModal extends window.NMSComponent {
  #eventListeners = [];

  constructor(container = document) {
    super();
    this.container = container;
    this.init();
  }

  init() {
    this.#cleanupEventListeners();
    this.bindEvents();
  }

  #cleanupEventListeners() {
    // Clean up existing event listeners to prevent duplicates
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];
  }

  bindEvents() {
    // Handle multiple modal instances within this container
    const modalTriggers = this.container.querySelectorAll('[data-nms-js="description-modal-trigger"]');
    const modals = this.container.querySelectorAll('[data-nms-js="description-modal"]');
    const modalBackdrops = this.container.querySelectorAll('[data-nms-js="modal-backdrop"]');
    const modalCloses = this.container.querySelectorAll('[data-nms-js="modal-close"]');

    if (!modalTriggers.length || !modals.length) return;

    // Store all modal elements
    this.modals = Array.from(modals);
    this.modalTriggers = Array.from(modalTriggers);
    this.modalBackdrops = Array.from(modalBackdrops);
    this.modalCloses = Array.from(modalCloses);

    // Bind event listeners for each trigger-modal pair
    this.modalTriggers.forEach((trigger, index) => {
      const modal = this.modals[index];
      if (modal) {
        const clickHandler = e => this.openModal(e, modal);
        trigger.addEventListener('click', clickHandler);
        this.#eventListeners.push({ element: trigger, event: 'click', handler: clickHandler });
      }
    });

    // Bind backdrop close events
    this.modalBackdrops.forEach((backdrop, index) => {
      const modal = this.modals[index];
      if (backdrop && modal) {
        const clickHandler = () => this.closeModal(modal);
        backdrop.addEventListener('click', clickHandler);
        this.#eventListeners.push({ element: backdrop, event: 'click', handler: clickHandler });
      }
    });

    // Bind close button events
    this.modalCloses.forEach((closeBtn, index) => {
      const modal = this.modals[index];
      if (closeBtn && modal) {
        const clickHandler = () => this.closeModal(modal);
        closeBtn.addEventListener('click', clickHandler);
        this.#eventListeners.push({ element: closeBtn, event: 'click', handler: clickHandler });
      }
    });

    // Close modal on escape key (only bind once globally)
    if (this.container === document) {
      const keydownHandler = this.handleKeydown.bind(this);
      document.addEventListener('keydown', keydownHandler);
      this.#eventListeners.push({ element: document, event: 'keydown', handler: keydownHandler });
    }
  }

  openModal(e, modal) {
    e.preventDefault();
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  closeModal(modal) {
    modal.style.display = 'none';
    document.body.style.overflow = '';
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      // Close any open modal
      this.modals.forEach(modal => {
        if (modal.style.display === 'flex') {
          this.closeModal(modal);
        }
      });
    }
  }

  destroy() {
    this.#cleanupEventListeners();
  }
}

// REQUIRED: Standard initialization pattern
function initNMSProductDescriptionModal(container = document) {
  if (!window.NMSComponent) {
    console.error('NMSProductDescriptionModal: NMSComponent required');
    return;
  }

  // Find all description modal components
  const elements = container.querySelectorAll('[data-nms-js="description-modal-trigger"]');

  if (elements.length > 0) {
    // Create a new modal instance for this container
    // Pass the container so events are bound to the right elements
    new NMSProductDescriptionModal(container);
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSProductDescriptionModal);
} else {
  initNMSProductDescriptionModal();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-js="description-modal-trigger"]')) {
    initNMSProductDescriptionModal(event.target);
  }
});

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container, source } = event.detail;
  initNMSProductDescriptionModal(container);
});

// Initialize component when DOM is ready
if (!window.neemans) window.neemans = {};
window.neemans.ProductDescriptionModal = NMSProductDescriptionModal;

// Global initialization function for other components to use
window.initNMSProductDescriptionModal = initNMSProductDescriptionModal;
